import './styles.css';
import builtInLogoUrl from './arrow-master-logo.webp';
import type { AudioManager } from './audio/audioManager';
import { BoardRenderer, PALETTES } from './boardRenderer';
import { canSlide } from '../core/generator';
import { ClearStreak } from '../core/clearStreak';
import { levelPackLabel, type LevelPack } from '../core/levelPack';
import type { Progress } from '../core/progress';
import { THEME_NAMES, type Attempt, type GameConfig, type Level, type Mode, type ThemeName } from '../core/types';
import { heart, icon } from './ui/icons';
import { TutorialController } from './ui/tutorial';

const $ = <T extends HTMLElement>(selector: string): T => {
  const found = document.querySelector<T>(selector);
  if (!found) throw new Error(`Missing HTML element: ${selector}`);
  return found;
};
const wait = (ms: number) => new Promise<void>(resolve => setTimeout(resolve, ms));
const formatTime = (seconds: number) => `${Math.floor(Math.max(0, seconds) / 60).toString().padStart(2, '0')}:${Math.floor(Math.max(0, seconds) % 60).toString().padStart(2, '0')}`;

type ModalAction = { text: string; kind?: 'primary' | 'gold' | 'ghost' | 'danger'; disabled?: boolean; callback: () => void | Promise<void> };

/** Game events for analytics (`analytics.track`); props are flat. */
export type TrackFn = (name: string, props?: Record<string, string | number | boolean>) => void;

export interface AppDeps {
  progress: Progress;
  audio: AudioManager;
  /** Wall clock in ms, corrected by the server time (the same clock as `progress`). */
  now: () => number;
  track: TrackFn;
  /** Published levels the player gets from the current release (content bundle `levels`). */
  levels: LevelPack | undefined;
  /** Lobby logo from content bundle `main` (alias `logo`), if the release has one. */
  logoUrl?: string;
}

export class ArrowMasterApp {
  private readonly backend: Progress;
  private readonly audio: AudioManager;
  private readonly now: () => number;
  private readonly track: TrackFn;
  private levels: LevelPack | undefined;
  private logoUrl: string;
  private board = new BoardRenderer();
  private mode: Mode = 'regular';
  private level: Level | null = null;
  private attempt: Attempt | null = null;
  private modalOpen = false;
  private completed = false;
  private pausedAt: number | null = null;
  private warningPlayed = false;
  private lastTimerSecond = -1;
  private busy = false;
  private winTimeout: number | null = null;
  private clearStreak = new ClearStreak();
  private streakTimeout: number | null = null;
  private inLobby = true;
  private entering = false;
  private entranceStartedAt = 0;
  private freezeEntranceClock = false;
  private tutorial: TutorialController | null = null;

  constructor(deps: AppDeps) {
    this.backend = deps.progress;
    this.audio = deps.audio;
    this.now = deps.now;
    this.track = deps.track;
    this.levels = deps.levels;
    this.logoUrl = deps.logoUrl ?? builtInLogoUrl;
  }

  /** New tunables from a release; the HUD shows new prices at once. */
  setConfig(config: GameConfig): void {
    this.backend.setConfig(config);
    this.updateHud();
  }

  /** New published levels: they apply to new attempts; a saved puzzle can switch in Settings. */
  setLevels(levels: LevelPack | undefined): void {
    this.levels = levels;
    this.backend.setLevels(levels?.catalog);
  }

  /** Lobby logo from OTA content (or back to the built-in one). */
  setLogo(url: string | undefined): void {
    this.logoUrl = url ?? builtInLogoUrl;
    const img = document.querySelector<HTMLImageElement>('.lobby-logo');
    if (img) img.src = this.logoUrl;
  }

  /** Online state from the session: a message while reconnecting or in maintenance, with an optional action (e.g. play here). */
  setConnection(online: boolean, message: string, action?: () => void): void {
    const banner = $('#connection');
    banner.textContent = message;
    if (action) {
      const button = document.createElement('button');
      button.className = 'connection-action';
      button.textContent = 'Play here';
      button.addEventListener('click', action);
      banner.append(' ', button);
    }
    banner.classList.toggle('hidden', online || !message);
  }

  async init(host: HTMLElement): Promise<void> {
    host.innerHTML = `
      <div class="app"><div class="phone in-lobby" id="phone">
        <section class="lobby" id="lobby" aria-label="Arrow Master lobby">
          <div class="lobby-top"><span class="eyebrow">THE JOURNEY</span><button class="icon-btn" id="lobby-settings" disabled aria-label="Lobby settings">${icon('settings')}</button></div>
          <div class="lobby-hero">
            <h1 class="lobby-brand"><img class="lobby-logo" src="${this.logoUrl}" alt="Arrow Master" width="1676" height="939" fetchpriority="high" draggable="false"></h1><p>A clear path is waiting.</p>
          </div>
          <div class="lobby-journey" id="lobby-journey" aria-label="Level progress"></div>
          <div class="lobby-actions"><button class="lobby-play" id="play-level" disabled><span id="play-label">Play</span><small id="play-level-label">Level 1</small></button><button class="daily-btn lobby-daily" id="lobby-daily" disabled>${icon('calendar')} Daily Challenge</button></div>
          <div class="lobby-footer"><span id="lobby-progress">Your journey starts here</span><span class="coin-balance">${icon('coin')}<span id="lobby-coins">75</span></span></div>
        </section>
        <header class="topbar">
          <button class="icon-btn back-btn" id="pause" title="Pause" aria-label="Pause menu">${icon('pause')}</button>
          <div class="level-heading"><span class="eyebrow">ARROW MASTER</span><h1 class="level-title" id="level-label">Level 1</h1></div>
          <div class="top-links">
            <button class="icon-btn" id="themes" title="Themes" aria-label="Arrow themes">${icon('palette')}</button>
            <button class="icon-btn" id="settings" title="Settings" aria-label="Settings">${icon('settings')}</button>
          </div>
        </header>
        <div class="sr-only"><span id="mode-label">The journey</span><span id="description">Find a clear path for every arrow</span></div>
        <div class="stats">
          <div class="lives" id="lives" role="img" aria-label="3 lives remaining"></div>
          <div class="clock-block"><span class="clock-label">TIME LEFT</span><div class="timer-value" id="timer">--:--</div><div class="timer-track"><div class="timer-fill" id="timer-fill"></div></div></div>
          <button class="hint-btn icon-btn" id="hint" title="Hint · 15 coins" aria-label="Hint · 15 coins">${icon('hint')}<span class="action-sub">15</span></button>
        </div>
        <div class="clear-progress"><span id="progress-label">CLEAR THE BOARD</span><span id="progress-count">0 / 0</span><div class="progress-track" id="clear-progress" role="progressbar" aria-label="Arrows cleared" aria-valuemin="0"><div id="progress-fill"></div></div>
          <div class="streak-popup hidden" id="streak-popup" role="status" aria-live="polite" aria-atomic="true"><span class="streak-count" aria-hidden="true">4</span><span class="sr-only">Four quick clears. </span><strong>Great streak!</strong><span class="streak-spark" aria-hidden="true">✦</span></div>
        </div>
        <p class="level-tip hidden" id="level-tip"></p>
        <main class="board-panel" id="board" aria-label="Arrow puzzle board"></main>
        <footer class="bottom-bar">
          <button class="daily-btn" id="daily">${icon('calendar')}<span id="daily-label">Daily</span><span class="sr-only" id="daily-sub">Challenge</span></button>
          <div class="coin-balance" aria-label="Coin balance">${icon('coin')}<span id="coins">75</span></div>
        </footer>
        <div class="hidden" id="modal-root"></div>
        <div class="toast hidden" id="toast"></div>
        <div class="connection hidden" id="connection" role="status" aria-live="polite"></div>
      </div></div>`;
    await this.board.init($('#board'));
    window.addEventListener('pointerdown', () => { void this.audio.unlock().catch(() => undefined); }, { once: true });
    $('#hint').addEventListener('click', () => this.hint());
    $('#daily').addEventListener('click', () => this.switchMode());
    $('#pause').addEventListener('click', () => this.pause());
    $('#settings').addEventListener('click', () => this.settings());
    $('#themes').addEventListener('click', () => this.themes());
    $('#play-level').addEventListener('click', () => { this.audio.play('button'); this.enter('regular'); });
    $('#lobby-daily').addEventListener('click', () => { this.audio.play('button'); this.enter('daily'); });
    $('#lobby-settings').addEventListener('click', () => this.settings());
    window.setInterval(() => this.tick(), 120);
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) this.resetStreak();
      // Prototype default: regular timers pause while backgrounded; daily timers are always wall clock.
      // For closed regular sessions, deadline reconciliation remains TBD in the PRD.
      if (document.hidden && this.mode === 'regular' && this.attempt?.status === 'playing' && !this.pausedAt) this.pausedAt = this.now();
      else if (!document.hidden && this.mode === 'regular' && this.pausedAt && !this.modalOpen && !this.entering) this.resumeClock();
    });
    this.attempt = this.backend.getAttempt('regular');
    this.showLobby();
  }
  private showLobby(): void {
    this.resetStreak();
    if (this.winTimeout) { clearTimeout(this.winTimeout); this.winTimeout = null; }
    if (this.mode === 'regular' && this.attempt?.status === 'playing' && this.pausedAt === null) this.pausedAt = this.now();
    this.inLobby = true;
    this.closeModal();
    this.board.clear();
    this.setEntering(false);
    $('#phone').classList.add('in-lobby');
    $('#lobby').classList.remove('hidden');
    this.updateLobby();
    this.applyTheme();
    document.querySelectorAll<HTMLButtonElement>('#lobby button').forEach(button => { button.disabled = false; });
  }
  private updateLobby(): void {
    const player = this.backend.getPlayer();
    const next = this.backend.nextLevel();
    const saved = this.backend.getAttempt('regular');
    $('#play-label').textContent = next > this.backend.config.maxLevel ? 'Journey complete' : saved ? 'Continue' : 'Play';
    $('#play-level-label').textContent = next > this.backend.config.maxLevel ? 'Explore the Daily Challenge' : `Level ${next}`;
    $('#lobby-coins').textContent = player.coins.toLocaleString();
    $('#lobby-daily').classList.toggle('hidden', !this.backend.config.dailyChallenge);
    $('#lobby-progress').textContent = player.completedIds.length ? `${player.completedIds.length} levels cleared` : 'Your journey starts here';
    const start = Math.min(Math.max(1, next - 1), this.backend.config.maxLevel - 4);
    $('#lobby-journey').innerHTML = Array.from({ length: 5 }, (_, i) => {
      const number = start + i;
      return `<span class="journey-stop ${number < next ? 'cleared' : number === next ? 'current' : ''}" ${number === next ? 'aria-current="step"' : ''}>${number < next ? '✓' : number}</span>`;
    }).join('');
  }
  private setEntering(value: boolean): void {
    this.entering = value;
    $('#phone').classList.toggle('entering-level', value);
    $('#board').setAttribute('aria-busy', String(value));
    document.querySelectorAll<HTMLElement>('.topbar, .stats, .bottom-bar').forEach(element => { element.inert = value; });
  }
  private enter(mode: Mode, forceNew = false): void {
    if (this.tutorial?.active) return;
    if (this.backend.needsTutorial()) { this.startTutorial(mode); return; }
    this.inLobby = false;
    this.resumeClock();
    this.setEntering(false);
    $('#phone').classList.remove('in-lobby');
    $('#lobby').classList.add('hidden');
    this.resetStreak();
    this.backend.rollDaily();
    if (this.modalOpen) this.closeModal();
    this.mode = mode;
    this.completed = false;
    this.warningPlayed = false;
    this.pausedAt = null;
    if (this.winTimeout) { clearTimeout(this.winTimeout); this.winTimeout = null; }
    if (mode === 'regular' && this.backend.nextLevel() > this.backend.config.maxLevel) {
      this.attempt = null; this.level = null; this.board.clear(); this.updateHud(); this.endOfContent(); return;
    }
    if (mode === 'daily' && this.backend.getPlayer().dailyCompleted) {
      this.attempt = null; this.level = null; this.board.clear(); this.updateHud();
      this.showModal('Daily cleared! ✨', 'You have already completed today’s challenge. A new puzzle arrives at 00:00 UTC.', [{ text: 'Back to levels', kind: 'primary', callback: () => this.enter('regular') }]);
      return;
    }
    if (mode === 'daily' && !this.backend.canStartDaily() && !this.backend.getAttempt('daily')) {
      this.toast(this.backend.config.dailyChallenge ? `All ${this.backend.config.dailyMaxAttempts} Daily Challenge attempts used. Try again tomorrow.` : 'The Daily Challenge is resting today.');
      this.enter('regular'); return;
    }
    const saved = forceNew ? null : this.backend.getAttempt(mode);
    const number = saved?.mode === 'regular' ? Number(saved.levelId.replace('level-', '')) : this.backend.nextLevel();
    this.level = this.backend.getLevel(mode, number);
    const resuming = saved && saved.levelId === this.level.id && saved.seed === this.level.seed;
    if (resuming) this.attempt = saved;
    else this.attempt = this.backend.begin(mode, this.level);
    if (!this.attempt) { this.enter('regular'); return; }
    this.track(resuming ? 'level_resume' : 'level_start', this.levelProps());
    this.board.setLevel(this.level, this.attempt.remainingArrowIds, id => this.arrowTapped(id), this.backend.getPlayer().theme, () => this.audio.play('exit'));
    this.applyTheme(); this.updateHud();
    if (mode === 'regular' && this.hasOlderPuzzle()) this.toast('Continuing your saved puzzle. Settings can switch it to the current release.');
    if (this.attempt.rulesUpdated) {
      this.toast('This puzzle restarted for the new arrow rules. Your coins and completed levels are kept.');
      delete this.attempt.rulesUpdated;
      this.backend.updateAttempt(this.attempt);
    }
    if (this.attempt.remainingArrowIds.length === 0 && this.attempt.status === 'playing') {
      this.completed = true;
      this.winTimeout = window.setTimeout(() => this.complete(), 250);
    }
    if (this.attempt.status === 'failed') this.gameOver(false);
    else if (!this.completed && this.attempt.deadline > this.now()) {
      const attempt = this.attempt;
      this.entranceStartedAt = this.now();
      this.freezeEntranceClock = mode === 'regular' || !resuming;
      this.setEntering(true);
      this.tick();
      this.board.playEntrance(() => {
        if (this.attempt !== attempt) return;
        if (this.freezeEntranceClock) {
          attempt.deadline += this.now() - this.entranceStartedAt;
          this.backend.updateAttempt(attempt);
        }
        // Background time was already accounted for by the entrance above.
        this.pausedAt = document.hidden && mode === 'regular' ? this.now() : null;
        this.setEntering(false);
        this.lastTimerSecond = -1;
        this.tick();
      });
    }
  }
  private switchMode(): void {
    if (this.inLobby || this.entering || this.modalOpen || this.busy) return;
    this.audio.play('button');
    this.enter(this.mode === 'regular' ? 'daily' : 'regular');
  }
  private arrowTapped(id: string): void {
    if (this.tutorial?.active) return;
    if (this.inLobby || this.entering || this.modalOpen || this.busy || this.completed || !this.level || !this.attempt || this.attempt.status !== 'playing') return;
    if (this.now() >= this.attempt.deadline && this.pausedAt === null) { this.fail('Time is up!'); return; }
    const a = this.level.arrows.find(a => a.id === id);
    if (!a || !this.attempt.remainingArrowIds.includes(id)) return;
    void this.audio.unlock().catch(() => undefined);
    this.audio.play('tap'); this.audio.haptic(8);
    const active = new Set(this.level.active.map(c => `${c.x},${c.y}`));
    const occupied = new Set(this.level.arrows.filter(a => this.attempt?.remainingArrowIds.includes(a.id)).flatMap(a => a.cells.map(c => `${c.x},${c.y}`)));
    if (canSlide(a, active, occupied)) {
      this.board.slide(id); this.audio.play('slide');
      this.attempt.remainingArrowIds = this.attempt.remainingArrowIds.filter(other => other !== id);
      this.backend.updateAttempt(this.attempt); this.updateHud();
      if (this.clearStreak.record(performance.now())) this.showStreak();
      if (this.attempt.remainingArrowIds.length === 0) {
        this.completed = true;
        this.winTimeout = window.setTimeout(() => this.complete(), 800);
      }
    } else {
      this.resetStreak();
      this.board.wobble(id); this.attempt.lives--; this.attempt.mistakes = (this.attempt.mistakes ?? 0) + 1;
      this.audio.play('blocked'); this.audio.haptic([20, 25, 35]);
      this.backend.updateAttempt(this.attempt); this.updateHud();
      this.track('arrow_blocked', { ...this.levelProps(), lives: this.attempt.lives });
      if (this.attempt.lives <= 0) this.fail('Out of lives!');
    }
  }
  private complete(): void {
    if (!this.level || !this.attempt) return;
    const current = this.attempt;
    const tier = Math.min(4, Math.floor((Math.max(1, this.level.number) - 1) / 20));
    const perfect = current.lives >= this.backend.config.lives && !current.reviveUsed && (current.mistakes ?? 0) === 0;
    const reward = current.mode === 'daily' ? this.backend.config.dailyReward :
      this.backend.config.rewardByTier[tier]! + (perfect ? this.backend.config.perfectBonus : 0) + this.backend.config.firstClearBonus;
    const props = this.levelProps();
    const granted = this.backend.finish(current.mode, reward);
    this.track('level_complete', { ...props, coins: granted, perfect, mistakes: current.mistakes ?? 0, revived: current.reviveUsed, seconds: Math.round((this.now() - current.startedAt) / 1000) });
    this.attempt = null;
    this.audio.play('complete'); this.audio.haptic([25, 45, 35]); this.updateHud();
    const details = current.mode === 'daily' ? 'Daily Challenge completed!' : (perfect ? 'Perfect clear — no mistakes!' : 'Every arrow found its way.');
    this.showModal('Level cleared!', `${details}<br><strong class="golden">+${granted} coins</strong>`, [
      { text: current.mode === 'daily' ? 'Back to levels' : 'Next level', kind: 'primary', callback: () => this.enter('regular') },
      ...(this.backend.config.rewardedAds && granted > 0 ? [{ text: 'Collect ×2 (mock rewarded ad)', kind: 'gold' as const, callback: async () => {
        const extra = await this.mockAd();
        if (extra) {
          const bonus = this.backend.award(granted, `double:${current.levelId}`);
          this.track('reward_doubled', { ...props, coins: bonus });
          this.updateHud(); this.toast(`Bonus +${bonus} coins!`);
        }
        this.enter('regular');
      } }] : [])
    ]);
    $('.modal').classList.add('victory');
    $('.modal-icon').innerHTML = icon('trophy');
  }
  private fail(reason: string): void {
    if (!this.attempt || this.attempt.status === 'failed' || this.completed) return;
    this.attempt.status = 'failed'; this.backend.updateAttempt(this.attempt);
    this.track('level_fail', { ...this.levelProps(), reason: this.attempt.lives <= 0 ? 'lives' : 'time', left: this.attempt.remainingArrowIds.length });
    this.audio.play('fail'); this.audio.haptic([30, 45, 45]); this.gameOver(true, reason);
  }
  private gameOver(_fresh: boolean, reason = 'This puzzle got the better of you.'): void {
    if (!this.attempt) return;
    this.updateHud();
    const canRevive = !this.attempt.reviveUsed;
    const coins = this.backend.getPlayer().coins;
    const isDaily = this.mode === 'daily';
    const canRetry = !isDaily || this.backend.canStartDaily();
    const actions: ModalAction[] = [
      { text: isDaily ? 'Try another attempt' : 'Retry puzzle', kind: 'primary', disabled: !canRetry, callback: () => this.retry() },
      { text: `Revive · ${this.backend.config.reviveCost} coins`, kind: 'gold', disabled: !canRevive || coins < this.backend.config.reviveCost, callback: () => this.revive('coins') },
      ...(this.backend.config.rewardedAds ? [{ text: 'Revive · watch ad (mock)', disabled: !canRevive, callback: () => void this.revive('ad') }] : []),
      { text: isDaily ? 'Back to regular levels' : 'Daily Challenge', kind: 'ghost', callback: () => this.enter(isDaily ? 'regular' : 'daily') }
    ];
    const timedOut = this.attempt.lives > 0 && this.attempt.deadline <= this.now();
    this.showModal(timedOut ? 'Time is up' : 'Out of lives', reason, actions, timedOut ? '⌛' : '💔');
  }
  private retry(): void {
    if (!this.level) return;
    if (this.mode === 'daily' && !this.backend.canStartDaily()) { this.toast('No attempts left today.'); return; }
    // Keep the saved generation version available until begin replaces the
    // attempt, so retry also reproduces puzzles from older client versions.
    this.enter(this.mode, true);
  }
  private async revive(how: 'coins' | 'ad'): Promise<void> {
    if (!this.attempt || this.attempt.reviveUsed) return;
    if (how === 'coins' && !this.backend.spend(this.backend.config.reviveCost)) return;
    if (how === 'ad' && !await this.mockAd()) return;
    this.attempt.reviveUsed = true;
    this.attempt.lives = this.backend.config.lives;
    this.attempt.status = 'playing';
    this.warningPlayed = false;
    this.attempt.deadline = this.now() + this.backend.config.reviveSeconds * 1000;
    this.backend.updateAttempt(this.attempt);
    this.track('revive', { ...this.levelProps(), how, coins: how === 'coins' ? this.backend.config.reviveCost : 0 });
    this.audio.play('revive'); this.audio.haptic(25); this.closeModal(); this.updateHud();
  }
  private async mockAd(): Promise<boolean> {
    this.busy = true;
    this.showModal('Mock rewarded ad', 'Simulating a successful ad completion. No ad network is connected.', []);
    await wait(1300);
    this.busy = false;
    return true;
  }
  private hint(): void {
    if (this.tutorial?.active) return;
    if (this.inLobby || this.entering || this.modalOpen || this.busy || this.completed || !this.level || !this.attempt || this.attempt.status !== 'playing') return;
    const occupied = new Set(this.level.arrows.filter(a => this.attempt?.remainingArrowIds.includes(a.id)).flatMap(a => a.cells.map(c => `${c.x},${c.y}`)));
    const active = new Set(this.level.active.map(c => `${c.x},${c.y}`));
    const valid = this.level.arrows.find(a => this.attempt?.remainingArrowIds.includes(a.id) && canSlide(a, active, occupied));
    if (!valid) { this.toast('No clear arrows available.'); return; }
    if (!this.backend.spend(this.backend.config.hintCost)) { this.toast('Not enough coins for a hint.'); return; }
    this.board.highlight(valid.id); this.audio.play('hint'); this.audio.haptic(12); this.updateHud();
    this.track('hint_used', { ...this.levelProps(), coins: this.backend.config.hintCost });
  }
  private tick(): void {
    this.backend.rollDaily();
    if (this.inLobby || this.tutorial?.active) return;
    if (this.mode === 'daily' && this.level?.id !== `daily-${this.backend.today()}`) { this.enter('regular'); this.toast('A new Daily Challenge is ready!'); return; }
    if (!this.attempt || this.attempt.status !== 'playing' || this.completed) return;
    const clock = this.entering && this.freezeEntranceClock ? this.entranceStartedAt : (this.pausedAt ?? this.now());
    const seconds = Math.max(0, Math.ceil((this.attempt.deadline - clock) / 1000));
    if (seconds !== this.lastTimerSecond) {
      this.lastTimerSecond = seconds;
      $('#timer').textContent = formatTime(seconds);
      const urgent = seconds <= 15;
      $('#timer').classList.toggle('urgent', urgent);
      $('#timer').classList.toggle('critical', seconds > 0 && seconds <= 10 && this.pausedAt === null && !this.modalOpen && !this.entering);
      const fill = $('#timer-fill');
      fill.style.width = `${Math.max(0, Math.min(100, seconds / this.attempt.timeLimit * 100))}%`;
      fill.classList.toggle('urgent', urgent);
      if (seconds <= 10 && seconds > 0 && (!this.warningPlayed || seconds <= 5) && !document.hidden && this.pausedAt === null && !this.modalOpen && !this.entering) {
        this.warningPlayed = true; this.audio.play('warning');
      }
    }
    if (seconds <= 0 && this.pausedAt === null && !this.entering) this.fail('Time is up!');
  }
  private updateHud(): void {
    if (this.inLobby) this.updateLobby();
    const player = this.backend.getPlayer();
    $('#coins').textContent = player.coins.toLocaleString();
    $('#daily').classList.toggle('hidden', !this.backend.config.dailyChallenge && this.mode === 'regular');
    const total = this.level?.arrows.length ?? 0;
    const cleared = this.completed ? total : this.attempt ? total - this.attempt.remainingArrowIds.length : 0;
    $('#progress-count').textContent = `${cleared} / ${total}`;
    $('#progress-label').textContent = this.completed ? 'BOARD CLEARED' : 'CLEAR THE BOARD';
    $('#progress-fill').style.width = `${total ? cleared / total * 100 : 0}%`;
    $('#clear-progress').setAttribute('aria-valuemax', String(total));
    $('#clear-progress').setAttribute('aria-valuenow', String(cleared));
    const lives = this.attempt?.lives ?? 0;
    $('#lives').innerHTML = Array.from({ length: Math.max(lives, this.backend.config.lives) }, (_, i) => `<span class="life ${i < lives ? '' : 'empty'}">${heart}</span>`).join('');
    $('#lives').setAttribute('aria-label', `${lives} lives remaining`);
    $('#mode-label').textContent = this.mode === 'daily' ? 'TODAY’S PUZZLE' : 'THE JOURNEY';
    $('#level-label').textContent = this.mode === 'daily' ? 'Daily Challenge' : this.level ? `Level ${this.level.number}` : 'Journey Complete';
    $('#description').textContent = this.mode === 'daily' ? `${this.backend.today()} · ${this.backend.config.dailyMaxAttempts - player.dailyAttempts} attempts left` : (this.level ? `${this.level.shape.replace('-', ' ').toUpperCase()} · Difficulty ${this.level.difficulty} / 5` : 'You have completed every available level');
    $('#level-tip').textContent = this.level?.introTip ?? '';
    $('#level-tip').classList.toggle('hidden', !this.level?.introTip);
    $('#daily-label').textContent = this.mode === 'daily' ? 'Levels' : 'Daily';
    $('#daily-sub').textContent = this.mode === 'daily' ? 'BACK' : player.dailyCompleted ? 'COMPLETE' : 'CHALLENGE';
    $('#hint').querySelector('.action-sub')!.textContent = `${this.backend.config.hintCost}`;
    $('#hint').setAttribute('aria-label', `Hint · ${this.backend.config.hintCost} coins`);
    $('#hint').title = `Hint · ${this.backend.config.hintCost} coins`;
    ($('#hint') as HTMLButtonElement).disabled = !this.attempt || this.attempt.status !== 'playing' || player.coins < this.backend.config.hintCost;
    if (!this.attempt) { $('#timer').textContent = '--:--'; $('#timer-fill').style.width = '0%'; }
    else this.lastTimerSecond = -1;
  }
  private applyTheme(): void {
    const theme = PALETTES[this.backend.getPlayer().theme];
    const phone = $('#phone');
    phone.style.setProperty('--bg', theme.background);
    phone.style.setProperty('--accent', theme.accent);
    ($<HTMLElement>('.app')).style.setProperty('--bg', theme.background);
    this.board.setTheme(this.backend.getPlayer().theme);
  }
  private pause(): void {
    if (this.inLobby || this.entering || !this.attempt || this.attempt.status !== 'playing' || this.modalOpen) return;
    this.audio.play('button');
    if (this.mode === 'regular') this.pausedAt = this.now();
    this.showModal('Paused', this.mode === 'daily' ? 'The Daily Challenge timer continues while paused.' : 'Take your time. Your timer is paused.', [
      { text: 'Resume puzzle', kind: 'primary', callback: () => { this.closeModal(); this.resumeClock(); } },
      { text: 'Back to lobby', callback: () => this.showLobby() },
      { text: 'Themes', callback: () => this.themes() },
      { text: 'Audio settings', callback: () => this.settings() }
    ], 'Ⅱ');
  }
  private resumeClock(): void {
    if (this.inLobby || this.tutorial?.active) return;
    if (this.pausedAt && this.attempt && this.mode === 'regular') {
      this.attempt.deadline += this.now() - this.pausedAt;
      this.backend.updateAttempt(this.attempt);
    }
    this.pausedAt = null;
    this.lastTimerSecond = -1;
  }
  private themes(): void {
    if (this.entering) return;
    if (!this.inLobby && !this.modalOpen && this.mode === 'regular' && this.attempt?.status === 'playing') this.pausedAt = this.now();
    const player = this.backend.getPlayer();
    const content = THEME_NAMES.map(name => {
      const cost = this.backend.config.themePrices[name];
      const owned = player.unlockedThemes.includes(name);
      const selected = player.theme === name;
      return `<div class="theme-row"><span class="theme-dot" style="background:#${PALETTES[name].arrow.toString(16).padStart(6,'0')}"></span><strong>${name}</strong><span class="theme-status">${selected ? 'Equipped' : owned ? 'Owned' : `${cost} coins`}</span><button data-theme="${name}" ${selected || (!owned && player.coins < cost) ? 'disabled' : ''}>${selected ? '✓' : owned ? 'Equip' : 'Unlock'}</button></div>`;
    }).join('');
    this.showModal('Arrow Themes', content, [{ text: 'Back to puzzle', kind: 'ghost', callback: () => { this.closeModal(); this.resumeClock(); } }], '🎨');
    $('#modal-root').querySelectorAll<HTMLButtonElement>('[data-theme]').forEach(button => button.addEventListener('click', () => {
      const name = button.dataset.theme as ThemeName;
      const owned = this.backend.getPlayer().unlockedThemes.includes(name);
      if (this.backend.theme(name)) {
        this.track(owned ? 'theme_equip' : 'theme_unlock', { theme: name, coins: owned ? 0 : this.backend.config.themePrices[name] });
        this.audio.play('coin'); this.applyTheme(); this.themes(); this.updateHud();
      }
    }));
  }
  private settings(): void {
    if (this.entering) return;
    if (!this.inLobby && !this.modalOpen && this.mode === 'regular' && this.attempt?.status === 'playing') this.pausedAt = this.now();
    const prefs = this.audio.prefs;
    const content = `<div class="setting-row"><span>🎵 Background music</span><input class="toggle" type="checkbox" aria-label="Background music" data-setting="music" ${prefs.music ? 'checked' : ''}></div><div class="setting-row"><span>🔊 Sound effects</span><input class="toggle" type="checkbox" aria-label="Sound effects" data-setting="sfx" ${prefs.sfx ? 'checked' : ''}></div><div class="setting-row"><span>📳 Haptics</span><input class="toggle" type="checkbox" aria-label="Haptics" data-setting="haptics" ${prefs.haptics ? 'checked' : ''}></div>`;
    this.showModal('Settings', `<p id="game-release">${levelPackLabel(this.levels)}</p>${this.hasOlderPuzzle() ? '<p id="saved-puzzle-note">Your saved puzzle is from an older release.</p>' : ''}${content}`, [
      ...(this.hasOlderPuzzle() ? [{ text: 'Use current level', callback: () => this.useCurrentLevel() }] : []),
      { text: 'How to play', callback: () => this.startTutorial() },
      { text: 'Themes', callback: () => this.themes() },
      { text: 'Reset game', kind: 'danger', callback: () => this.confirmReset() },
      { text: this.inLobby ? 'Back to lobby' : 'Resume', kind: 'primary', callback: () => { this.closeModal(); this.resumeClock(); } }
    ], '⚙');
    $('#modal-root').querySelectorAll<HTMLInputElement>('[data-setting]').forEach(input => input.addEventListener('change', () => {
      const setting = input.dataset.setting as 'music' | 'sfx' | 'haptics';
      this.audio.set(setting, input.checked);
    }));
  }
  private hasOlderPuzzle(): boolean {
    const saved = this.backend.getAttempt('regular');
    if (!saved) return false;
    const number = Number(saved.levelId.replace('level-', ''));
    const current = this.backend.currentLevel(number);
    return JSON.stringify(this.backend.getLevel('regular', number)) !== JSON.stringify(current);
  }
  private useCurrentLevel(): void {
    this.showModal('Use the current puzzle?', 'This restarts only your saved regular puzzle with the current release. Coins, completed levels, themes and Daily Challenge progress are kept.', [
      { text: 'Cancel', callback: () => this.settings() },
      { text: 'Use current level', kind: 'primary', callback: () => {
        this.backend.clearAttempt('regular');
        this.attempt = null;
        this.enter('regular', true);
      } }
    ]);
  }
  private startTutorial(destination: Mode | null = null): void {
    if (this.tutorial?.active || this.entering || this.busy) return;
    const returnToLobby = this.inLobby;
    if (this.mode === 'regular' && this.attempt?.status === 'playing' && this.pausedAt === null) this.pausedAt = this.now();
    this.closeModal();
    this.resetStreak();
    this.tutorial ??= new TutorialController($('#phone'), this.audio);
    const doneLabel = destination === 'daily' ? 'Start Daily Challenge' : destination === 'regular'
      ? `Play Level ${this.backend.nextLevel()}` : returnToLobby ? 'Back to lobby' : 'Back to game';
    void this.tutorial.start(this.backend.getPlayer().theme, this.backend.config.hintCost, doneLabel, status => {
      this.backend.finishTutorial(status);
      this.track('tutorial_finish', { status });
      if (destination) this.enter(destination);
      else {
        this.resumeClock();
        this.updateHud();
        this.tick();
        $(returnToLobby ? '#play-level' : '#settings').focus();
      }
    }, this.mode === 'daily' && this.attempt?.status === 'playing').catch(error => {
      console.error(error);
      if (destination) this.showLobby();
      else this.resumeClock();
      this.toast('Practice could not load. Please try How to play again.');
    });
  }
  private confirmReset(): void {
    this.showModal('Start over?', `This will erase your level progress, Daily Challenge progress, coins, and unlocked themes. You’ll restart at Level 1 with ${this.backend.config.startingCoins} coins. Sound and haptic settings will stay the same. This cannot be undone.`, [
      { text: 'Cancel', kind: 'primary', callback: () => this.settings() },
      { text: 'Reset and start over', kind: 'danger', callback: () => {
        this.backend.resetProgress();
        this.track('progress_reset');
        this.attempt = null;
        this.pausedAt = null;
        $('#toast').classList.add('hidden');
        this.enter('regular');
      } }
    ], '↺');
  }
  private endOfContent(): void {
    this.showModal('Journey complete! 🏆', 'You have cleared all 100 available levels. The Daily Challenge is still waiting!', [
      { text: 'Daily Challenge', kind: 'primary', callback: () => this.enter('daily') },
      { text: 'Settings', callback: () => this.settings() }
    ]);
  }
  private showModal(title: string, body: string, actions: ModalAction[], icon = '✦'): void {
    this.resetStreak();
    this.modalOpen = true;
    $('#timer').classList.remove('critical');
    const modalRoot = $('#modal-root');
    modalRoot.classList.remove('hidden');
    modalRoot.innerHTML = `<div class="modal-backdrop"><section class="modal" role="dialog" aria-modal="true" aria-label="${title}"><div class="modal-icon">${icon}</div><h2>${title}</h2><div class="modal-copy">${body}</div><div class="modal-actions" id="modal-actions"></div></section></div>`;
    const actionHost = modalRoot.querySelector<HTMLElement>('#modal-actions')!;
    for (const action of actions) {
      const button = document.createElement('button');
      button.className = `modal-btn ${action.kind ?? ''}`;
      button.textContent = action.text;
      button.disabled = Boolean(action.disabled);
      button.addEventListener('click', () => {
        if (this.busy) return;
        this.audio.play('button');
        const result = action.callback();
        if (result instanceof Promise) void result.catch(error => this.toast(String(error)));
      });
      actionHost.appendChild(button);
    }
  }
  private closeModal(): void { $('#modal-root').innerHTML = ''; $('#modal-root').classList.add('hidden'); this.modalOpen = false; }
  private showStreak(): void {
    if (this.streakTimeout !== null) clearTimeout(this.streakTimeout);
    const popup = $('#streak-popup');
    popup.classList.add('hidden');
    void popup.offsetWidth; // Restart the entrance animation for a new group of four.
    popup.classList.remove('hidden');
    this.streakTimeout = window.setTimeout(() => {
      popup.classList.add('hidden');
      this.streakTimeout = null;
    }, 1500);
  }
  private resetStreak(): void {
    this.clearStreak.reset();
    if (this.streakTimeout !== null) clearTimeout(this.streakTimeout);
    this.streakTimeout = null;
    $('#streak-popup').classList.add('hidden');
  }
  /** Which board the player is on, for analytics. */
  private levelProps(): Record<string, string | number> {
    return this.level ? { mode: this.mode, level: this.level.number, shape: this.level.shape, arrows: this.level.arrows.length, difficulty: this.level.difficulty } : { mode: this.mode };
  }
  private toast(message: string): void {
    const toast = $('#toast'); toast.textContent = message; toast.classList.remove('hidden');
    window.setTimeout(() => { if (toast.textContent === message) toast.classList.add('hidden'); }, 2600);
  }
}
